import { Box, Card, CardContent, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Briefcase, Building2, Globe, Headset, Mail, MapPin, Phone } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { HeadquartersMap, HQ_MAP_ZOOM } from "../../../components/HeadquartersMap.js";
import { PageHeader } from "../../../components/ui/page-header.js";
import { XOP_CONTACT, XOP_HQ, XOP_TEL_HREF } from "../../../lib/company.js";
import { osmPlaceUrl } from "../../../lib/osmTiles.js";
import { PUBLIC_ROUTES } from "../../public/content.js";

/**
 * Contact — how a reseller reaches XOP Networks (2026-09-15).
 *
 * Until this page existed the whole contact block (address, phone, both
 * mailboxes, website) was printed in the foot of the left rail, where it
 * competed with the navigation and was cut off on a short screen. The rail
 * now ends with one "Contact XOP Networks" link to here, plus the legal links
 * and the copyright.
 *
 * Every value comes from `lib/company.ts` — the one copy of XOP's details in
 * this app. The page adds only WHICH mailbox to use for what, because "sales@
 * or support@?" is the question a reseller actually has, and the headquarters
 * on a free OpenStreetMap map (`components/HeadquartersMap.tsx`). The support
 * portal has the same page, leading with Support instead of Sales.
 *
 * No permission gates it: every signed-in person may contact XOP.
 */
export function ContactPage(): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 3 }} data-testid="contact-page">
      <PageHeader
        title="Contact XOP Networks"
        description="Who to reach for sales, for support, and where to find us."
      />

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
        <ContactCard
          testId="contact-sales"
          icon={<Briefcase size={18} />}
          title="Sales"
          summary="Quotes, pricing, new customers and prospects, and your reseller agreement."
        >
          <ContactLine icon={<Mail size={15} />} href={`mailto:${XOP_CONTACT.sales}`}>
            {XOP_CONTACT.sales}
          </ContactLine>
          <ContactLine icon={<Phone size={15} />} href={XOP_TEL_HREF}>
            {XOP_CONTACT.phone}
          </ContactLine>
        </ContactCard>

        <ContactCard
          testId="contact-support"
          icon={<Headset size={18} />}
          title="Support"
          summary="Technical help with an installed system, licences, releases and upgrades."
        >
          <ContactLine icon={<Mail size={15} />} href={`mailto:${XOP_CONTACT.support}`}>
            {XOP_CONTACT.support}
          </ContactLine>
          <ContactLine icon={<Phone size={15} />} href={XOP_TEL_HREF}>
            {XOP_CONTACT.phone}
          </ContactLine>
        </ContactCard>
      </Box>

      <Card
        variant="outlined"
        data-testid="contact-headquarters"
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 3fr)" } }}
      >
        <CardContent sx={{ display: "grid", gap: 1.5, alignContent: "start", p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <CardTitle icon={<Building2 size={18} />} title="Headquarters" />
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5 }}>{XOP_CONTACT.company}</Typography>
          <Box sx={{ display: "grid", gap: 0.75 }}>
            <ContactLine icon={<MapPin size={15} />} href={osmPlaceUrl(XOP_HQ.lat, XOP_HQ.lon, HQ_MAP_ZOOM)} external>
              {XOP_CONTACT.addressLine}
            </ContactLine>
            <ContactLine icon={<Phone size={15} />} href={XOP_TEL_HREF}>
              {XOP_CONTACT.phone}
            </ContactLine>
            <ContactLine icon={<Globe size={15} />} href={`https://${XOP_CONTACT.website}`} external>
              {XOP_CONTACT.website}
            </ContactLine>
          </Box>
        </CardContent>
        <HeadquartersMap
          sx={{
            height: { xs: 280, md: "auto" },
            borderTop: { xs: 1, md: 0 },
            borderLeft: { xs: 0, md: 1 },
            borderColor: "divider",
          }}
        />
      </Card>

      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        Using this portal is subject to the{" "}
        <Link component={NavLink} to={PUBLIC_ROUTES.terms}>Terms of Use</Link> and the{" "}
        <Link component={NavLink} to={PUBLIC_ROUTES.privacy}>Privacy Policy</Link>.
      </Typography>
    </Box>
  );
}

function CardTitle({ icon, title }: { icon: ReactNode; title: string }): JSX.Element {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box
        aria-hidden
        sx={{
          display: "grid",
          placeItems: "center",
          width: 34,
          height: 34,
          borderRadius: 1,
          flexShrink: 0,
          color: "primary.main",
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
        }}
      >
        {icon}
      </Box>
      <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
  );
}

function ContactCard({
  testId,
  icon,
  title,
  summary,
  children,
}: {
  testId: string;
  icon: ReactNode;
  title: string;
  summary: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card variant="outlined" data-testid={testId} sx={{ height: "100%" }}>
      <CardContent sx={{ display: "grid", gap: 1.5, p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <CardTitle icon={icon} title={title} />
        <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5 }}>{summary}</Typography>
        <Box sx={{ display: "grid", gap: 0.75 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function ContactLine({
  icon,
  href,
  external = false,
  children,
}: {
  icon: ReactNode;
  href: string;
  external?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
      <Box aria-hidden sx={{ display: "flex", color: "text.disabled", mt: "2px", flexShrink: 0 }}>
        {icon}
      </Box>
      <Link
        href={href}
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
        sx={{ fontSize: 14, color: "text.primary", overflowWrap: "anywhere", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
      >
        {children}
      </Link>
    </Box>
  );
}
